import { useSelector } from "react-redux";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import { Link, useLocation } from "react-router-dom";
import { Users01 as NewSupervisorAccountIcon } from "@untitledui/icons";
import { useIntl } from "../../../../_metronic/i18n/customUseIntl";
import { headerMenusStyles } from "../../../../_metronic/layout/_core/MaterialThemeProvider";
import QuickUserToggler from "../../../../_metronic/layout/components/extras/QuiclUserToggler";
import Notifications from "../notifications/NotificationBell";
import NotificationDropdown from "../../../../_metronic/layout/components/extras/dropdowns/notifications/NotificationDropdown";
import Can from "../../../modules/Auth/Can";
import useAdobeDataLayer from "../../../sustell_15/analytics/adobeDataLayer";
import featureFlags from "../../utils/featureFlags";

export const MainMenu = () => {
  const classes = headerMenusStyles();
  const selectedCustomer = useSelector((state) => state.selectedCustomer);
  const feedV2Route = featureFlags.feedV2Routing
    ? `/v2/feeds?customerId=${selectedCustomer?.value?.id}`
    : `/customers/${selectedCustomer?.value?.id}/myfeeds`;
  const myFarmsRoute = `/customers/${selectedCustomer?.value?.id}/myfarms`;
  const farmV2Route = `/v2/farms?organizationId=${selectedCustomer?.value?.id}`;
  const ingredientsOverviewRoute = `/v2/ingredients?organizationId=${selectedCustomer?.value?.id}`;
  const backofficeCustomersRoute = featureFlags.backofficeM1
    ? "/backoffice"
    : "/admin/customers";
  const intl = useIntl();
  const location = useLocation();
  const { ctaClickEvent } = useAdobeDataLayer();
  const homepagePath = featureFlags.homepageFP ? '/v2/homepage' : '/homepage';

  const isActive = (url) => {
    switch (url) {
      case homepagePath:
        return location.pathname === url;
      case url.match(/^\/customers\/.*\/myfeeds$/)?.input:
        return (
          location.pathname === url ||
          location.pathname.startsWith(
            `/customers/${selectedCustomer?.value?.id}/compoundfeeds`,
          ) ||
          location.pathname.startsWith(`/feed-footprints`)
        );
      case url.match(/^\/customers\/.*\/myfarms$/)?.input:
        return (
          location.pathname === url ||
          location.pathname.startsWith(
            `/customers/${selectedCustomer?.value?.id}/farm`,
          ) ||
          location.pathname.startsWith(`/footprints`)
        );
      case url.match(/^\/customers\/.*\/facilities$/)?.input:
        return (
          location.pathname === url ||
          location.pathname.startsWith(
            `/customers/${selectedCustomer?.value?.id}/facilities`,
          )
        );
      case url.match(/^\/mydashboard/)?.input:
        return (
          location.pathname === url ||
          location.pathname.startsWith(`/mydashboard`)
        );
      default:
        return false;
    }
  };

  const urlActiveClass = (url) => (isActive(url) ? "active" : null);

  return (
    <div className={`container ${classes.mainMenu}`}>
      {Can("access", "Management pages") && (
        <Link
          to={homepagePath}
          onClick={() =>
            ctaClickEvent(homepagePath, "link", "Home", "Main Menu", "Header")
          }
          className={`${classes.mainMenuItem} ${urlActiveClass(homepagePath)}`}
        >
          {intl.formatMessage({ id: "SUSTELL.HOME" })}
        </Link>
      )}

      {Can("access", "Management pages") &&
        (
          <Link
            to={ingredientsOverviewRoute}
            onClick={() =>
              ctaClickEvent(
                ingredientsOverviewRoute,
                "link",
                "My Ingredients",
                "Main Menu",
                "Header",
              )
            }
            className={`${classes.mainMenuItem} ${urlActiveClass(
              ingredientsOverviewRoute,
            )}`}
          >
            {intl.formatMessage({ id: "SUSTELL.MY_INGREDIENTS" })}
          </Link>
        )}

      {Can("access", "Management pages") && !featureFlags.feedV2Routing && (
        <Link
          to={feedV2Route}
          onClick={() =>
            ctaClickEvent(
              feedV2Route,
              "link",
              "My Feeds",
              "Main Menu",
              "Header",
            )
          }
          className={`${classes.mainMenuItem} ${urlActiveClass(feedV2Route)}`}
        >
          {intl.formatMessage({ id: "SUSTELL.MY_FEEDS" })}
        </Link>
      )}

      {Can("access", "Management pages") && featureFlags.feedV2Routing && (
        <a
          href={`/v2/feeds?customerId=${selectedCustomer?.value?.id}`}
          onClick={() =>
            ctaClickEvent(
              `/v2/feeds?customerId=${selectedCustomer?.value?.id}`,
              "link",
              "My Feeds",
              "Main Menu",
              "Header",
            )
          }
          className={`${classes.mainMenuItem}`}
        >
          {intl.formatMessage({ id: "SUSTELL.MY_FEEDS" })}
        </a>
      )}

      {Can("access", "Management pages") && !featureFlags.farmV2Routing && (
        <Link
          to={myFarmsRoute}
          onClick={() =>
            ctaClickEvent(
              myFarmsRoute,
              "link",
              "My Farms",
              "Main Menu",
              "Header",
            )
          }
          className={`${classes.mainMenuItem} ${urlActiveClass(myFarmsRoute)}`}
        >
          {intl.formatMessage({ id: "SUSTELL.MY_FARMS" })}
        </Link>
      )}

      {Can("access", "Management pages") && featureFlags.farmV2Routing && (
        <a
          href={farmV2Route}
          onClick={() =>
            ctaClickEvent(
              farmV2Route,
              "link",
              "My Farms",
              "Main Menu",
              "Header",
            )
          }
          className={`${classes.mainMenuItem}`}
        >
          {intl.formatMessage({ id: "SUSTELL.MY_FARMS" })}
        </a>
      )}

      {Can("access", "Management pages") && (
        <Link
          to={`/customers/${selectedCustomer?.value?.id}/facilities`}
          onClick={() =>
            ctaClickEvent(
              `/customers/${selectedCustomer?.value?.id}/facilities`,
              "link",
              "My Facilities",
              "Main Menu",
              "Header",
            )
          }
          className={`${classes.mainMenuItem} ${urlActiveClass(
            `/customers/${selectedCustomer?.value?.id}/facilities`,
          )}`}
        >
          {intl.formatMessage({ id: "PROFILE.FACILITIES.TITLE" })}
        </Link>
      )}

      {Can("access", "Dashboard") && (
        <Link
          to="/mydashboard/overview"
          onClick={() =>
            ctaClickEvent(
              `/mydashboard/overview`,
              "link",
              "My Dashboard",
              "Main Menu",
              "Header",
            )
          }
          className={`${classes.mainMenuItem} ${urlActiveClass(
            "/mydashboard/overview",
          )}`}
        >
          {intl.formatMessage({ id: "SUSTELL.MY_DASHBOARD" })}
        </Link>
      )}
      {featureFlags.notifications ? (
        <Notifications useNewIcon />
      ) : (
        <NotificationDropdown />
      )}

      {(featureFlags.backofficeM1
        ? Can("access", "Admin pages")
        : Can("access", "Admin pages") || Can("read", "Customer")) && (
        <Link
          to={backofficeCustomersRoute}
          onClick={() =>
            ctaClickEvent(
              backofficeCustomersRoute,
              "link",
              "My Admin",
              "Main Menu",
              "Header",
            )
          }
          className={classes.mainMenuItemIcon}
        >
          <NewSupervisorAccountIcon
            className={`${classes.mainMenuItemProfile}`}
            width={20}
            height={20}
          />
        </Link>
      )}
      <QuickUserToggler className={classes.mainMenuItemIcon} />
    </div>
  );
};
